/**
 * `Result<T>` — explicit success/failure without throwing across boundaries.
 *
 * WHY: external/port calls must degrade into typed failures routed to Telemetry,
 * never throw into the UI. Callers branch on `ok` and the type narrows, so the
 * error path is impossible to forget.
 */

/** Coarse classification so callers/telemetry can react without parsing messages. */
export type ErrorKind =
  | 'cancelled' // the operation was aborted via AbortSignal
  | 'not_found' // a requested resource does not exist
  | 'validation' // input failed boundary parsing ("parse, don't validate")
  | 'io' // transport/storage failure
  | 'forbidden' // the api was reachable but denied the request (auth) — NOT a transient outage
  | 'unknown'; // anything unclassified

/** A classified failure carrying a human message and an optional cause. */
export interface AppError {
  readonly kind: ErrorKind;
  readonly message: string;
  readonly cause?: unknown;
}

export type Result<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: AppError };

/** Construct a success result. */
export function ok<T>(value: T): Result<T> {
  return { ok: true, value };
}

/** Construct a failure result. */
export function err(kind: ErrorKind, message: string, cause?: unknown): Result<never> {
  return cause === undefined ? { ok: false, error: { kind, message } } : { ok: false, error: { kind, message, cause } };
}

/**
 * Classify an unknown thrown value into an {@link AppError}.
 * Recognises AbortError (→ `cancelled`) so cancellation is first-class.
 */
export function classify(cause: unknown): AppError {
  if (cause instanceof DOMException && cause.name === 'AbortError') {
    return { kind: 'cancelled', message: 'Operation was cancelled', cause };
  }
  if (cause instanceof Error && cause.name === 'AbortError') {
    return { kind: 'cancelled', message: 'Operation was cancelled', cause };
  }
  const message = cause instanceof Error ? cause.message : 'Unknown error';
  return { kind: 'unknown', message, cause };
}

/**
 * Run an async operation, capturing any throw as a classified failure.
 * This is the wrapper that keeps `throw` out of the rest of the app.
 */
export async function attempt<T>(fn: () => Promise<T>): Promise<Result<T>> {
  try {
    return ok(await fn());
  } catch (cause) {
    return { ok: false, error: classify(cause) };
  }
}
